// libraries/VisibilityManager.js

import layoutEngine from '../libraries/layoutEngine';

const visibilityMap = new Map();  // Map of cellName -> true/false
const lockMap = {}; // { [cellName]: true/false }


/**
 * Return whether a cell is visible or not.
 */
export const isVisible = (cellName) => {
  return visibilityMap.get(cellName) ?? true;
};

/**
 * Toggle visibility and trigger a canvas redraw.
 */
export const toggleVisibility = (cellName) => {
  const current = isVisible(cellName);
  setVisibility(cellName, !current);
};

/**
 * Explicitly set a cell's visibility.
 */
export const setVisibility = (cellName, visible) => {
  visibilityMap.set(cellName, visible);
  window.canvasEngine.draw(); // Trigger re-render
};

export function isLocked(cellName) {
  return !!lockMap[cellName];
}

export function toggleLock(cellName) {
  lockMap[cellName] = !lockMap[cellName];
}

export function setLock(cellName, value) {
  lockMap[cellName] = value;
}

export function getLockMap() {
  return { ...lockMap };
}


export function drawVisibilityOverlays(ctx, scale, offsetX, offsetY, onlyCellName = null) {

  ctx.save();
  ctx.translate(offsetX,offsetY);
  ctx.scale(scale,scale);
  ctx.beginPath();
  ctx.moveTo(-100000, 0);
  ctx.lineTo(100000, 0);
  ctx.moveTo(0, -100000);
  ctx.lineTo(0, 100000);
  ctx.lineWidth = 1 / scale;
  ctx.strokeStyle = "#ff0000";
  ctx.stroke();
  ctx.restore();
  const cellsToDraw = onlyCellName ? [onlyCellName] : Object.keys(visibilityMap);

  for (const cellName of cellsToDraw) {
    if (!visibilityMap[cellName]) {
      const cell = layoutEngine.getCellByName(cellName);
      if (!cell) continue;

      const bounds = getBoundingBox(cell);
      if (!bounds) continue;

      const { x, y, x2, y2 } = bounds;
      const width = x2 - x;
      const height = y2 - y;
      const centerX = (x + x2) / 2;
      const centerY = (y + y2) / 2;

      ctx.save();
      ctx.translate(offsetX, offsetY);
      ctx.scale(scale, scale);

      // 🔷 Bounding box
      ctx.strokeStyle = '#00ffff';
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(x, y, width, height);
      ctx.setLineDash([]);

      // 🏷️ Cell name
      ctx.fillStyle = '#00ffff';
      ctx.font = '12px monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(cellName, centerX, centerY);

      ctx.restore();
    }
  }
}

/**
 * Compute bounding box from elements (rectangles only).
 */
function getBoundingBox(cell, visited = new Set()) {
  if (!cell || visited.has(cell.name)) return null;
  visited.add(cell.name);

  const points = [];

  // 🔷 Include element bounds
  for (const el of cell.elements || []) {
    if (el.type === 'rectangle') {
      const x = el.x ?? 0;
      const y = el.y ?? 0;
      const w = el.width ?? 0;
      const h = el.height ?? 0;
      points.push([x, y], [x + w, y + h]);
    } else if (el.type === 'polygon') {
      points.push(...(el.points || []));
    } else if (el.type === 'label') {
      points.push([el.x, el.y]);
    }
  }

  // 🔷 Include child instances recursively
  for (const inst of cell.instances || []) {
    const child = layoutEngine.getCellByName(inst.ref);
    if (!child) continue;

    const bbox = getBoundingBox(child, visited);
    if (!bbox) continue;

    const angle = (inst.rotation || 0) * Math.PI / 180;
    const scale = inst.scale || 1;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const corners = [
      [bbox.x, bbox.y],
      [bbox.x2, bbox.y],
      [bbox.x, bbox.y2],
      [bbox.x2, bbox.y2],
    ];

    const transformed = corners.map(([cx, cy]) => {
      const dx = cx * scale;
      const dy = cy * scale;
      return [
        inst.x + dx * cos - dy * sin,
        inst.y + dx * sin + dy * cos,
      ];
    });

    points.push(...transformed);
  }

  if (points.length === 0) return null;

  const xs = points.map(p => p[0]);
  const ys = points.map(p => p[1]);
  return {
    x: Math.min(...xs),
    y: Math.min(...ys),
    x2: Math.max(...xs),
    y2: Math.max(...ys)
  };
}


